/**
 * Session Helper Utilities
 * Manages session timeout and validation for student and guardian authentication
 */

import type { Student, Guardian } from './types';

/**
 * Session timeout duration in milliseconds
 * Default: 1 hour (3600000 ms)
 */
export const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

/**
 * Extended Student Session with timestamp and JWT token
 */
export interface StudentSession {
  student: Student;
  loginTime: number; // Unix timestamp in milliseconds
  token?: string; // JWT from Edge Function (for authenticated requests)
}

/**
 * Store student session with login timestamp and optional JWT token
 * @param student - The authenticated student
 * @param token - Optional JWT token from Edge Function
 */
export function setStudentSession(student: Student, token?: string): void {
  const session: StudentSession = {
    student,
    loginTime: Date.now(),
    token,
  };
  localStorage.setItem('student_session', JSON.stringify(session));
}

/**
 * Store JWT token in existing session
 * @param token - JWT from Edge Function
 */
export function setStudentToken(token: string): void {
  const sessionData = localStorage.getItem('student_session');
  if (sessionData) {
    const session: StudentSession = JSON.parse(sessionData);
    session.token = token;
    localStorage.setItem('student_session', JSON.stringify(session));
  }
}

/**
 * Get JWT token from student session
 * @returns JWT token or null if not found/expired
 */
export function getStudentToken(): string | null {
  try {
    const sessionData = localStorage.getItem('student_session');
    if (!sessionData) return null;

    const session: StudentSession = JSON.parse(sessionData);

    // Check session expiry
    if (!session.loginTime || Date.now() - session.loginTime > SESSION_TIMEOUT_MS) {
      return null;
    }

    return session.token || null;
  } catch {
    return null;
  }
}

/**
 * Get student session if valid (not expired)
 * @returns Student object if session is valid, null if expired or not found
 */
export function getStudentSession(): Student | null {
  try {
    const sessionData = localStorage.getItem('student_session');
    if (!sessionData) {
      return null;
    }

    const session: StudentSession = JSON.parse(sessionData);

    // Check if session has required fields
    if (!session.loginTime || !session.student) {
      console.warn('Invalid session format, clearing session');
      clearStudentSession();
      return null;
    }

    // Check if session has expired
    const currentTime = Date.now();
    const sessionAge = currentTime - session.loginTime;

    if (sessionAge > SESSION_TIMEOUT_MS) {
      console.log('Student session expired, clearing session');
      clearStudentSession();
      return null;
    }

    return session.student;
  } catch (error) {
    console.error('Error reading student session:', error);
    clearStudentSession();
    return null;
  }
}

/**
 * Check if student session exists and is valid
 * @returns true if valid session exists, false otherwise
 */
export function hasValidStudentSession(): boolean {
  return getStudentSession() !== null;
}

/**
 * Clear student session from localStorage
 */
export function clearStudentSession(): void {
  localStorage.removeItem('student_session');
}

/**
 * Get remaining session time in milliseconds
 * @returns Remaining time in ms, or 0 if no valid session
 */
export function getRemainingSessionTime(): number {
  try {
    const sessionData = localStorage.getItem('student_session');
    if (!sessionData) {
      return 0;
    }

    const session: StudentSession = JSON.parse(sessionData);
    if (!session.loginTime) {
      return 0;
    }

    const currentTime = Date.now();
    const sessionAge = currentTime - session.loginTime;
    const remaining = SESSION_TIMEOUT_MS - sessionAge;

    return remaining > 0 ? remaining : 0;
  } catch (error) {
    return 0;
  }
}

/**
 * Format remaining time as human-readable string
 * @returns Formatted time string (e.g., "45 minutes", "30 seconds")
 */
export function formatRemainingTime(): string {
  const remainingMs = getRemainingSessionTime();
  if (remainingMs === 0) {
    return 'Session expired';
  }

  const minutes = Math.floor(remainingMs / (60 * 1000));
  const seconds = Math.floor((remainingMs % (60 * 1000)) / 1000);

  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

// ─── Guardian Session ───────────────────────────────────────────────────────

/**
 * Guardian session timeout: 8 hours (a typical working/school day)
 */
export const GUARDIAN_SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;

export interface GuardianSession {
  guardian: Guardian;
  loginTime: number;
}

/**
 * Persist guardian profile to localStorage so pages can load without a DB
 * round-trip on every navigation.
 */
export function setGuardianSession(guardian: Guardian): void {
  const session: GuardianSession = { guardian, loginTime: Date.now() };
  localStorage.setItem('guardian_session', JSON.stringify(session));
}

/**
 * Return the cached guardian if the session is still valid, null otherwise.
 */
export function getGuardianSession(): Guardian | null {
  try {
    const raw = localStorage.getItem('guardian_session');
    if (!raw) return null;

    const session: GuardianSession = JSON.parse(raw);
    if (!session.loginTime || !session.guardian) {
      clearGuardianSession();
      return null;
    }

    if (Date.now() - session.loginTime > GUARDIAN_SESSION_TIMEOUT_MS) {
      clearGuardianSession();
      return null;
    }

    return session.guardian;
  } catch {
    clearGuardianSession();
    return null;
  }
}

/**
 * Remove guardian session from localStorage (call on logout or auth failure).
 */
export function clearGuardianSession(): void {
  localStorage.removeItem('guardian_session');
}
